import React, { useState, useEffect } from 'react'
import { Calendar, MapPin, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

interface PFASEligibilityDetailsProps {
  enrollmentData: any
  updateEnrollmentData: (field: string, value: any) => void
  handleNavigate: (stepIndex: number) => Promise<void>
  isNavigating?: boolean
}

const CANCER_TYPES = [
  { value: 'testicular', label: 'Testicular Cancer' },
  { value: 'kidney', label: 'Kidney Cancer' }
]

export default function PFASEligibilityDetails({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  isNavigating = false
}: PFASEligibilityDetailsProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Form fields
  const [cancerType, setCancerType] = useState(
    enrollmentData.pfasCancerType || 
    enrollmentData.diagnosisType || 
    enrollmentData.pfasDiagnosisType || 
    ''
  )
  const [diagnosisDate, setDiagnosisDate] = useState(enrollmentData.pfasDiagnosisDate || '')
  const [isDeceased, setIsDeceased] = useState(enrollmentData.pfasIsDeceased || false)
  const [deathDate, setDeathDate] = useState(enrollmentData.pfasDeathDate || '')
  const [residenceStartDate, setResidenceStartDate] = useState(enrollmentData.pfasResidenceStartDate || '')
  const [residenceEndDate, setResidenceEndDate] = useState(enrollmentData.pfasResidenceEndDate || '')

  // Auto-populate cancer type from previous form data
  useEffect(() => {
    const previousDiagnosisType = enrollmentData.diagnosisType || enrollmentData.pfasDiagnosisType
    
    // Only auto-populate if current cancer type is empty and we have previous diagnosis type
    if (!enrollmentData.pfasCancerType && previousDiagnosisType && previousDiagnosisType !== cancerType) {
      setCancerType(previousDiagnosisType)
      updateEnrollmentData('pfasCancerType', previousDiagnosisType)
    }
  }, [enrollmentData.diagnosisType, enrollmentData.pfasDiagnosisType, enrollmentData.pfasCancerType, cancerType, updateEnrollmentData])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Cancer type validation
    if (!cancerType) {
      newErrors.cancerType = 'Please select a cancer type'
    } else if (!['testicular', 'kidney'].includes(cancerType)) {
      newErrors.cancerType = 'Cancer type must be testicular or kidney cancer'
    }

    // Diagnosis date validation
    if (!diagnosisDate) {
      newErrors.diagnosisDate = 'Please enter the diagnosis date'
    } else {
      const diagnosis = new Date(diagnosisDate)
      const now = new Date()
      const yearsAgo = (now.getTime() - diagnosis.getTime()) / (1000 * 60 * 60 * 24 * 365)
      
      if (yearsAgo > 20) {
        newErrors.diagnosisDate = 'Diagnosis must be within the last 20 years'
      }
      if (yearsAgo < 0) {
        newErrors.diagnosisDate = 'Diagnosis date cannot be in the future'
      }
    }

    // Death date validation (if deceased)
    if (isDeceased) {
      if (!deathDate) {
        newErrors.deathDate = 'Please enter the date of death'
      } else {
        const death = new Date(deathDate)
        const now = new Date()
        const yearsAgo = (now.getTime() - death.getTime()) / (1000 * 60 * 60 * 24 * 365)
        
        if (yearsAgo > 7) {
          newErrors.deathDate = 'Death must have occurred within the last 7 years'
        }
        if (yearsAgo < 0) {
          newErrors.deathDate = 'Death date cannot be in the future'
        }

        // Death date must be after diagnosis date
        if (diagnosisDate && death < new Date(diagnosisDate)) {
          newErrors.deathDate = 'Death date must be after diagnosis date'
        }
      }
    }

    // Residence duration validation
    if (!residenceStartDate) {
      newErrors.residenceStartDate = 'Please enter when you started living at this zipcode'
    } else {
      const start = new Date(residenceStartDate)
      const end = residenceEndDate ? new Date(residenceEndDate) : new Date()
      const yearsResided = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365)
      
      if (yearsResided < 1) {
        newErrors.residenceStartDate = 'You must have lived in this zipcode for at least 1 year'
      }
      if (start > end) {
        newErrors.residenceEndDate = 'End date must be after start date'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleContinue = async () => {
    if (!validateForm()) {
      return
    }

    // Save all data
    updateEnrollmentData('pfasCancerType', cancerType)
    updateEnrollmentData('pfasDiagnosisDate', diagnosisDate)
    updateEnrollmentData('pfasIsDeceased', isDeceased)
    updateEnrollmentData('pfasDeathDate', isDeceased ? deathDate : null)
    updateEnrollmentData('pfasResidenceStartDate', residenceStartDate)
    updateEnrollmentData('pfasResidenceEndDate', residenceEndDate || new Date().toISOString().split('T')[0])

    await handleNavigate(3)
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-accent-900">Eligibility Details</h2>
            <p className="text-sm text-accent-600">Provide information to verify your eligibility</p>
          </div>
        </div>

        {/* Eligibility Criteria Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Eligibility Requirements</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
            <li>Diagnosed with testicular cancer OR kidney cancer (max 20 years ago)</li>
            <li>If deceased: must have passed away max 7 years ago</li>
            <li>Must have lived in eligible zipcode for at least 1 year</li>
            <li>No age restrictions</li>
          </ul>
        </div>

        {/* Cancer Diagnosis Section */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-accent-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Cancer Diagnosis
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancer Type <span className="text-red-500">*</span>
              </label>
              <select
                value={cancerType}
                onChange={(e) => {
                  setCancerType(e.target.value)
                  updateEnrollmentData('pfasCancerType', e.target.value)
                  if (errors.cancerType) {
                    setErrors(prev => ({ ...prev, cancerType: '' }))
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.cancerType ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Select cancer type</option>
                {CANCER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.cancerType && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.cancerType}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Diagnosis Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={diagnosisDate}
                  onChange={(e) => {
                    setDiagnosisDate(e.target.value)
                    updateEnrollmentData('pfasDiagnosisDate', e.target.value)
                    if (errors.diagnosisDate) {
                      setErrors(prev => ({ ...prev, diagnosisDate: '' }))
                    }
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.diagnosisDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.diagnosisDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.diagnosisDate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Must be within the last 20 years
              </p>
            </div>

            {/* Deceased Status */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDeceased}
                  onChange={(e) => {
                    setIsDeceased(e.target.checked)
                    updateEnrollmentData('pfasIsDeceased', e.target.checked)
                    if (!e.target.checked) {
                      setDeathDate('')
                      updateEnrollmentData('pfasDeathDate', null)
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Victim is deceased
                </span>
              </label>
            </div>

            {isDeceased && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Death <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={deathDate}
                    onChange={(e) => {
                      setDeathDate(e.target.value)
                      updateEnrollmentData('pfasDeathDate', e.target.value)
                      if (errors.deathDate) {
                        setErrors(prev => ({ ...prev, deathDate: '' }))
                      }
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    min={diagnosisDate || undefined}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.deathDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.deathDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.deathDate}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Must be within the last 7 years and after diagnosis date
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Residence Duration Section */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-accent-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Residence Duration
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Started Living at Zipcode {enrollmentData.pfasZipcode || 'N/A'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={residenceStartDate}
                  onChange={(e) => {
                    setResidenceStartDate(e.target.value)
                    updateEnrollmentData('pfasResidenceStartDate', e.target.value)
                    if (errors.residenceStartDate) {
                      setErrors(prev => ({ ...prev, residenceStartDate: '' }))
                    }
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.residenceStartDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.residenceStartDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.residenceStartDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ended Living at This Zipcode (Leave blank if still living there)
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={residenceEndDate}
                  onChange={(e) => {
                    setResidenceEndDate(e.target.value)
                    updateEnrollmentData('pfasResidenceEndDate', e.target.value)
                    if (errors.residenceEndDate) {
                      setErrors(prev => ({ ...prev, residenceEndDate: '' }))
                    }
                  }}
                  min={residenceStartDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.residenceEndDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.residenceEndDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.residenceEndDate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Must have lived at this zipcode for at least 1 year
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-end mt-6">
          <button
            onClick={handleContinue}
            disabled={isNavigating}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
          >
            {isNavigating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <span>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

